import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LayoutDashboard, Building2, Users, Megaphone, Menu, X, LogOut, User, Radio, FileDown, Shield, UserCog } from 'lucide-react';
import { exportApi } from '@/lib/api';
import { toast } from 'sonner';

const Layout = () => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/fornitori', label: 'Fornitori (Radio)', icon: Radio },
    { path: '/clienti', label: 'Clienti', icon: Users },
    { path: '/campagne', label: 'Campagne', icon: Megaphone },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-toggle">
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </Button>
          <div className="flex items-center gap-2">
            <Radio className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg font-['Manrope']">RadioManager</span>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="user-menu-mobile"><User size={20} /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled><span className="text-sm text-muted-foreground">{user?.email}</span></DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} data-testid="logout-mobile"><LogOut className="mr-2 h-4 w-4" />Esci</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-40 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-16 flex items-center gap-3 px-6 border-b border-slate-200">
          <Radio className="h-7 w-7 text-primary" />
          <span className="font-bold text-xl font-['Manrope'] tracking-tight">RadioManager</span>
        </div>

        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.path.replace('/', '')}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-150 ${isActive(item.path) ? 'bg-primary/10 text-primary sidebar-item-active' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
            >
              <item.icon size={20} strokeWidth={1.5} />
              {item.label}
            </Link>
          ))}

          {/* Admin Section */}
          {isAdmin && (
            <>
              <div className="pt-4 pb-2">
                <p className="px-4 text-xs font-semibold text-slate-400 uppercase">Amministrazione</p>
              </div>
              <Link
                to="/admin/users"
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-admin-users"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-150 ${isActive('/admin/users') ? 'bg-primary/10 text-primary sidebar-item-active' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
              >
                <UserCog size={20} strokeWidth={1.5} />
                Gestione Utenti
              </Link>
            </>
          )}
        </nav>

        {/* Export Section */}
        <div className="absolute bottom-24 left-0 right-0 px-4">
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-500 mb-3 font-medium">ESPORTA DATI</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => { exportApi.downloadFornitoriCsv(); toast.success('Export completato'); }} data-testid="export-fornitori-btn">
                <FileDown size={14} className="mr-1" />Fornitori
              </Button>
              <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => { exportApi.downloadCampagneCsv(); toast.success('Export completato'); }} data-testid="export-campagne-btn">
                <FileDown size={14} className="mr-1" />Campagne
              </Button>
            </div>
          </div>
        </div>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
              <User size={18} className="text-primary" />
            </div>
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-slate-900">{user?.nome}</p>
                {isAdmin && <Badge className="bg-purple-100 text-purple-700 text-xs"><Shield size={10} className="mr-1" />Admin</Badge>}
              </div>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full text-slate-600 hover:text-red-600 hover:border-red-200" onClick={handleLogout} data-testid="logout-btn">
            <LogOut size={16} className="mr-2" />Esci dall'account
          </Button>
        </div>
      </aside>

      {sidebarOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setSidebarOpen(false)} />}

      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen"><Outlet /></main>
    </div>
  );
};

export default Layout;
